import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { MegaMenuItem } from "@/data/navigation";

interface MegaMenuDropdownProps {
  item: MegaMenuItem;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}

const MegaMenuDropdown = ({ item, isOpen, onOpen, onClose }: MegaMenuDropdownProps) => {
  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
    >
      <button
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors
          ${isOpen ? "text-primary bg-primary/5" : "text-muted-foreground hover:text-foreground"}`}
      >
        {item.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-1/2 -translate-x-1/2 pt-2 z-50"
          >
            <div className="rounded-lg border border-border shadow-2xl shadow-black/40 dark:shadow-black/60 p-4 min-w-[420px] bg-popover/95 backdrop-blur-xl">
              <div className="grid grid-cols-2 gap-4">
                {item.groups.map((group) => (
                  <div key={group.label}>
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2 px-2">
                      {group.label}
                    </p>
                    <div className="space-y-0.5">
                      {group.items.map((sub) => (
                        <Link
                          key={sub.href}
                          to={sub.href}
                          className="flex items-start gap-3 px-2 py-2 rounded-md text-sm hover:bg-accent/50 transition-colors group"
                          onClick={onClose}
                        >
                          <sub.icon className="h-4 w-4 mt-0.5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-foreground group-hover:text-primary transition-colors text-sm leading-tight">
                              {sub.label}
                            </p>
                            {sub.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                                {sub.description}
                              </p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MegaMenuDropdown;
